import { Directive, computed, input } from '@angular/core';
import { cva, type VariantProps } from 'class-variance-authority';

import { MatRipple } from '@angular/material/core';
import * as fromAtomsColors from '../atoms/colors';
import * as fromHelpers from '../helpers';

export enum FloatingActionButtonVariant {
  filled = 'filled',
  tonal = 'tonal',
}

export enum FloatingActionButtonSizeVariant {
  xs = 'xs',
  sm = 'sm',
  md = 'md',
  lg = 'lg',
  xl = 'xl',
}

export enum FloatingActionButtonColorVariant {
  primary = fromAtomsColors.ColorVariant.primary,
  neutral = fromAtomsColors.ColorVariant.neutral,
}

export const floatingActionButtonVariants = cva(
  fromHelpers.cn(
    'inline-flex items-center justify-center',
    'rounded-full transition-all',
    'hover:cursor-pointer',
    'disabled:opacity-50 disabled:pointer-events-none',
    'shadow-xxs',
    '[&>svg]:shrink-0',
    'border border-grayscale-gray-light',
  ),
  {
    variants: {
      variant: {
        [`${FloatingActionButtonVariant.filled}`]: '',
        [`${FloatingActionButtonVariant.tonal}`]: '',
      },
      color: {
        [`${FloatingActionButtonColorVariant.primary}`]: '',
        [`${FloatingActionButtonColorVariant.neutral}`]: '',
      },
      size: {
        [`${FloatingActionButtonSizeVariant.xs}`]:
          'min-w-32 min-h-32 [&>svg]:size-20',
        [`${FloatingActionButtonSizeVariant.sm}`]:
          'min-w-40 min-h-40 [&>svg]:size-20',
        [`${FloatingActionButtonSizeVariant.md}`]:
          'min-w-48 min-h-48 [&>svg]:size-24',
        [`${FloatingActionButtonSizeVariant.lg}`]:
          'min-w-56 min-h-56 [&>svg]:size-28',
        [`${FloatingActionButtonSizeVariant.xl}`]:
          'min-w-64 min-h-64 [&>svg]:size-32',
      },
    },
    defaultVariants: {
      variant: FloatingActionButtonVariant.filled,
      color: FloatingActionButtonColorVariant.primary,
      size: FloatingActionButtonSizeVariant.md,
    },
    compoundVariants: [
      {
        variant: FloatingActionButtonVariant.filled,
        color: FloatingActionButtonColorVariant.primary,
        class: fromHelpers.cn(
          'bg-button-primary-filled-enabled text-shades-white',
          'hover:bg-button-primary-filled-hovered',
          'focus-visible:bg-button-primary-filled-focused',
          'active:bg-button-primary-filled-pressed',
          'disabled:bg-button-primary-filled-disabled',
        ),
      },
      {
        variant: FloatingActionButtonVariant.filled,
        color: FloatingActionButtonColorVariant.neutral,
        class: fromHelpers.cn(
          'bg-shades-white text-primary-500',
          'hover:bg-grayscale-gray-lighter',
          'focus:bg-button-primary-filled',
          'active:bg-grayscale-gray-lighter',
          'disabled:bg-button-neutral-filled-disabled',
        ),
      },
    ],
  },
);
export type FloatingActionButtonVariants = VariantProps<
  typeof floatingActionButtonVariants
>;

@Directive({
  selector: '[dmcomFloatingActionButton]',
  standalone: true,
  host: {
    '[class]': 'className()',
  },
  hostDirectives: [
    {
      directive: MatRipple,
      inputs: [
        'matRippleColor',
        'matRippleDisabled',
        'matRippleCentered',
        'matRippleRadius',
        'matRippleAnimation',
      ],
    },
  ],
})
export class FloatingActionButtonDirective {
  readonly variant = input<FloatingActionButtonVariants['variant']>();
  readonly color = input<FloatingActionButtonVariants['color']>();
  readonly size = input<FloatingActionButtonVariants['size']>();

  protected readonly className = computed(() =>
    fromHelpers.cn(
      floatingActionButtonVariants({
        variant: this.variant(),
        color: this.color(),
        size: this.size(),
      }),
    ),
  );
}
